"use client";

import { useMemo, useState, type DragEvent } from "react";
import { Upload } from "lucide-react";

import { Spinner } from "@/components/UI/Spinner";
import { NoxusTooltip } from "@/components/UI/NoxusTooltip";
import { FileTypes, getFileTypeLabel, getMaxFileSize } from "@/utils/mimetypes";
import { cn } from "@/lib/utils";

interface FileDropTargetProps {
  onClick: () => void;
  onFiles: (files: FileList) => void;
  loading?: boolean;
  isDisabled?: boolean;
  allowedFileTypes?: FileTypes[];
  multiple?: boolean;
  className?: string;
}

export function FileDropTarget({
  onClick,
  onFiles,
  loading,
  isDisabled,
  allowedFileTypes,
  multiple = false,
  className,
}: FileDropTargetProps) {
  // Depth counter rather than a boolean: dragenter/dragleave fire for every
  // child crossed, and a boolean would flicker while the file moves over the
  // icon and text.
  const [dragDepth, setDragDepth] = useState(0);
  // A pending upload blocks new selections too, so a slower earlier upload
  // cannot land after (and overwrite) a newer one.
  const inactive = Boolean(isDisabled || loading);
  const isDragActive = dragDepth > 0 && !inactive;
  const maxFileSize = getMaxFileSize();

  const [firstFormats, remainingFormats] = useMemo(() => {
    const labels = Array.from(
      new Set(
        Object.values(FileTypes)
          .filter((t) => allowedFileTypes?.includes(t))
          .map(getFileTypeLabel)
      )
    );
    return labels.length <= 4
      ? [labels.join(", "), []]
      : [labels.slice(0, 4).join(", "), labels.slice(4)];
  }, [allowedFileTypes]);

  const handleDragEnter = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (inactive) return;
    setDragDepth((depth) => depth + 1);
  };

  const handleDragOver = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleDragLeave = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (inactive) return;
    setDragDepth((depth) => Math.max(0, depth - 1));
  };

  const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    // A drop on the target is consumed here; an ancestor that also accepts
    // drops (e.g. a dialog body) must not process the same files again.
    event.stopPropagation();
    setDragDepth(0);
    if (inactive) return;
    onFiles(event.dataTransfer.files);
  };

  return (
    <div className={cn("w-full", className)}>
      <button
        type="button"
        disabled={inactive}
        className={cn(
          "flex h-24 w-full flex-col items-center justify-center rounded-md border border-dashed bg-transparent text-center text-foreground transition-colors",
          "outline-none focus-visible:ring-ring focus-visible:ring-1",
          isDragActive ? "border-ring bg-accent/40" : "border-input",
          isDisabled && "opacity-50",
          inactive
            ? "cursor-not-allowed"
            : "cursor-pointer hover:border-ring hover:bg-accent/40"
        )}
        onClick={onClick}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {loading ? (
          <Spinner />
        ) : (
          <>
            <Upload size={24} strokeWidth={1.5} />
            <p className="mt-2 text-xs">
              Drag and drop {multiple ? "files" : "a file"} or{" "}
              <span className="font-semibold underline">
                Choose {multiple ? "files" : "file"}
              </span>
            </p>
          </>
        )}
      </button>

      {allowedFileTypes && allowedFileTypes.length > 0 && (
        <div className="mt-1 flex flex-wrap justify-between text-xs text-muted-foreground">
          <p>
            Supported formats: {firstFormats}
            {remainingFormats.length > 0 && (
              <NoxusTooltip asChild content={remainingFormats.join(", ")}>
                <span className="cursor-pointer text-muted-foreground">
                  {`, + ${remainingFormats.length} more`}
                </span>
              </NoxusTooltip>
            )}
          </p>
          <p>Maximum size: {maxFileSize / 1024 / 1024}MB</p>
        </div>
      )}
    </div>
  );
}
